<template>
	<div class="questionnaires__question question-card">
		<input type="hidden" :name="'questionnaire[questions_attributes][' + question_index + '][_destroy]'" v-model="question._destroy">
		<input type="hidden" :name="'questionnaire[questions_attributes][' + question_index + '][id]'" v-model="question.id">
		<input type="hidden" :name="'questionnaire[questions_attributes][' + question_index + '][pos]'" v-model="question.pos">
		<div class="question-card__row">
			<label class="labeled-field question-card__main-field">
				<input
					class="labeled-field__input"
					type="text"
					:name="'questionnaire[questions_attributes][' + question_index + '][title]'"
					:placeholder="t.title.placeholder + ' ' + (question_index + 1)"
					v-model.trim="question.title"
					data-test-id="questionnaire-question-title"
					@input="questionFieldUpdate(question)"
					@change="flushQuestionUpdate(question)"
				>
			</label>
			<label class="labeled-field labeled-field--select question-card__submain-field">
				<div class="labeled-field__input-wrap">
					<select class="labeled-field__input" v-model="question.question_type" @change="questionFieldUpdate(question)">
						<option value="one">{{ t.question_type.one }}</option>
						<option value="many">{{ t.question_type.many }}</option>
					</select>
				</div>
			</label>
		</div>

		<ul class="question-card__answers-list">
			<QuestionnairesAnswer
				v-for="(answer, answer_index) in question.answers"
				:key="'question.' + question_index + '.answer.' + answer_index"
				:answer="answer"
				:question_index="question_index"
				:answer_index="answer_index"
				:is_question_with_single_answer="isQuestionWithSingleAnswer(question)"
			/>
		</ul>

		<div class="question-card__footer">
			<div class="question-card__footer-left">
				<button class="link-btn" type="button" data-test-id="questionnaire-add-answer-btn" @click.prevent="addAnswer(question)" :disabled="!question.create_answer_url">
					{{ t.btns.add_answer }}
				</button>
				<span class="sub-light-font-color"> / </span>
				<button class="link-btn" type="button" @click.prevent="addAnswer(question, 'open')" :disabled="!question.create_answer_url || alreadyAddOpenAnswer(question)">
					{{ t.btns.add_other }}
				</button>
			</div>
			<div class="question-card__footer-right">
				<label class="checkbox">
					<input
						type="checkbox"
						class="checkbox__input"
						:name="'questionnaire[questions_attributes][' + question_index + '][required]'"
						v-model="question.required"
						@input="questionFieldUpdate(question)"
						:disabled="isQuestionTitleEmpty(question)"
						>
						<span class="checkbox__label">{{ t.required_field }}</span>
				</label>
				<button class="link-btn link-btn--red ml-25" type="button" @click.prevent="removeQuestion(question, question_index)">
					{{ t.btns.remove }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import { reactive } from 'vue';
import { debounce } from 'vue-debounce';
import QuestionnairesAnswer from './Answer.vue';

export default {
	name: 'QuestionnairesQuestion',

	components: { QuestionnairesAnswer },

	props: {
		question: { type: Object, required: true },
		question_index: { type: Number, required: true }
	},

	data() {
		return {
			state: '',
			t: I18n.t.questionnaire_dashboard_form.fields.questionnaire.questions
		}
	},

	created() {
		// Таймер свой у каждого вопроса: debounce, объявленный в methods, общий на все экземпляры
		// и отменял сохранение соседнего вопроса
		this.debouncedQuestionUpdate = debounce(question => this.updateQuestion(question), 800);
	},

	watch: {
		// Вопрос могли изменить раньше, чем сервер вернул адрес для его сохранения
		'question.update_url'() {
			if (this.state == 'modified') this.debouncedQuestionUpdate(this.question);
		}
	},

	methods: {
		isQuestionTitleEmpty(question) {
			return !question.title || question.title.length <= 0;
		},

		alreadyAddOpenAnswer(question) {
			return question.answers ? question.answers.filter(this.activeAnswer).some(answer => answer.answer_type == 'open') : false;
		},

		isQuestionWithSingleAnswer(question) {
			return question.question_type == 'one';
		},

		activeAnswer(answer) {
			return !answer._destroy;
		},

		questionFieldUpdate(question) {
			this.state = 'modified';
			this.debouncedQuestionUpdate(question);
		},

		// Уход с поля сохраняет сразу, не дожидаясь таймера
		flushQuestionUpdate(question) {
			if (this.state != 'modified') return;

			this.debouncedQuestionUpdate.cancel();
			this.updateQuestion(question);
		},

		updateQuestion(question) {
			if (!question.update_url) return;

			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
				},
				method: 'PATCH',
				url: question.update_url,
				data: { question }
			}).then(response => {
				this.state = 'synced'
			}).catch(errors => {
				this.state = 'error'
			})
		},

		removeQuestion(question, index) {
			question._destroy = true

			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
				},
				method: 'DELETE',
				url: question.update_url,
				data: {}
			}).then(response => {
				this.state = 'synced';
			}).catch(errors => {
				question._destroy = false;
				this.state = 'error';
			})
		},

		addAnswer(question, answer_type = 'default') {
			// reactive: адрес из ответа сервера должен запустить отложенное сохранение текста варианта
			let answer = reactive({ ...this.answerPlaceholder });
			answer.answer_type = answer_type;
			question.answers = question.answers.filter(this.activeAnswer);

			if (answer_type == 'open') {
				answer.title = 'Другое...';
				answer.pos = 9999;
				question.answers.splice(question.answers.length, 0, answer);
			} else {
				// Не «количество + 1»: после удаления варианта позиции повторялись бы
				answer.pos = Math.max(0, ...question.answers.filter(a => !this.isOpenAnswer(a)).map(a => a.pos)) + 1;
				if (this.alreadyAddOpenAnswer(question)) {
					question.answers.splice(-1, 0, answer);
				} else {
					question.answers.push(answer);
				}
			}

			axios({
				response_type: "json",
				headers: {
					"X-Requested-With": "XMLHttpRequest",
					"X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
				},
				method: 'POST',
				url: question.create_answer_url,
				data: { answer }
			}).then(response => {
				answer.id = response.data.answer.id;
				answer.update_url = response.data.answer.update_url;
				this.state = 'synced';
			}).catch(errors => {
				this.state = 'error';
			})
		},

		activeAnswer(answer) {
			return !answer._destroy;
		},

		isOpenAnswer(answer) {
			return answer.answer_type == 'open';
		},
	},

	computed: {
		sortedAnswers() {
			return this.question.answers.filter(a => !a._destroy).sort((a, b) => a.pos < b.pos);
		}
	},
}
</script>
